import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import PhotoSwipe from 'photoswipe';

import ContextMenuPortal from '../portals/ContextMenuPortal';

import 'photoswipe/style.css';

const PHOTOSWIPE_MAIN_CLASS = 'output-lightbox';
const ZERO_POSITION = { x: 0, y: 0 };

// treat anything that smells like a context-menu click as one for now.
const isContextMenuGesture = (event) => {
  if (!event) return false;
  if (event.type === 'contextmenu') return true;
  if (event.button === 2 || event.buttons === 2) return true;

  // todo: will this work on windows?
  return event.button === 0 && event.ctrlKey;
};

const getValidSlideDimensions = (slide) => {
  const width = slide?.width;
  const height = slide?.height;

  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return { width, height };
  }

  return null;
};

const getValidSlideSrc = (slide) => {
  if (slide?.src) {
    return slide.src;
  }

  return null;
};

const getValidSlidePublicUrl = (slide) => {
  if (slide?.publicUrl) {
    return slide.publicUrl;
  }

  return null;
};

const getRequiredSlidePdfUrl = (slide) => {
  if (slide?.pdfUrl) {
    return slide.pdfUrl;
  }

  throw new Error(`ImageAttachmentLightbox expected a slide pdfUrl for ${slide?.attachmentId || 'unknown slide'}`);
};

const getPdfLightboxDimensions = () => {
  if (typeof window !== 'undefined') {
    return {
      width: Math.max(1, Math.round(window.innerWidth * 0.8)),
      height: Math.max(1, Math.round(window.innerHeight * 0.9)),
    };
  }

  return { width: 1200, height: 1350 };
};

const buildPreparedPhotoSwipeSlide = (slide) => {
  if (slide?.type === 'pdf') {
    const pdfUrl = slide?.pdfUrl || null;
    if (!pdfUrl) return null;

    // Match the old PDF lightbox behavior: open the document in a large 80vw x 90vh box.
    const { width, height } = getPdfLightboxDimensions();

    return {
      type: 'pdf',
      attachmentId: slide?.attachmentId || null,
      pdfUrl,
      alt: slide?.alt || 'PDF',
      width,
      height,
      thumbBounds: slide?.thumbBounds || null,
    };
  }

  const src = getValidSlideSrc(slide);
  const publicUrl = getValidSlidePublicUrl(slide);
  const dimensions = getValidSlideDimensions(slide);

  if (!src || !publicUrl || !dimensions) return null;

  return {
    attachmentId: slide?.attachmentId || null,
    contentType: slide?.contentType || null,
    src,
    publicUrl,
    msrc: slide?.previewSrc || null,
    srcset: slide?.srcset || null,
    alt: slide?.alt || 'Image',
    width: dimensions.width,
    height: dimensions.height,
    thumbBounds: slide?.thumbBounds || null,
  };
};

const getPreparedSlideIndex = (preparedSlides, requestedIndex) => {
  if (preparedSlides.length === 0) return 0;

  const normalizedRequestedIndex = Number.isInteger(requestedIndex) ? requestedIndex : 0;
  const exactIndex = preparedSlides.findIndex(({ originalIndex }) => originalIndex === normalizedRequestedIndex);
  if (exactIndex >= 0) return exactIndex;

  const nextValidIndex = preparedSlides.findIndex(({ originalIndex }) => originalIndex > normalizedRequestedIndex);
  if (nextValidIndex >= 0) return nextValidIndex;

  return preparedSlides.length - 1;
};

const preparePhotoSwipeSlides = (slides, requestedIndex = 0) => {
  const preparedSlides = (slides || [])
    .map((slide, originalIndex) => {
      const preparedSlide = buildPreparedPhotoSwipeSlide(slide);
      return preparedSlide ? { originalIndex, slide: preparedSlide } : null;
    })
    .filter(Boolean);

  return {
    slides: preparedSlides.map(({ slide }) => slide),
    index: getPreparedSlideIndex(preparedSlides, requestedIndex),
  };
};

export const ImageAttachmentLightbox = ({
  open,
  onClose,
  src,
  publicUrl,
  previewSrc,
  srcset,
  alt = 'Image',
  width,
  height,
  thumbBounds,
  slides,
  index = 0,
  onShowInChat,
  channelId,
}) => {
  const photoSwipeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onShowInChatRef = useRef(onShowInChat);
  // Registration happens once per PhotoSwipe instance, so gate on presence (stable)
  // and invoke through the ref to avoid re-initializing on callback identity changes.
  const hasShowInChat = Boolean(onShowInChat);
  const shouldNotifyParentOnCloseRef = useRef(true);
  const isMountedRef = useRef(true);
  const [instanceVersion, setInstanceVersion] = useState(0);
  const [contextMenu, setContextMenu] = useState(null);

  const handleCloseContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleBeforeRemix = useCallback(() => {
    const photoSwipe = photoSwipeRef.current;
    if (photoSwipe) photoSwipe.close();
  }, []);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onShowInChatRef.current = onShowInChat;
  }, [onShowInChat]);

  const resolvedSlides = useMemo(() => {
    if (slides) {
      return slides.map((slide, slideIndex) =>
        slideIndex === index && thumbBounds && !slide?.thumbBounds ? { ...slide, thumbBounds } : slide,
      );
    }

    return [
      {
        src,
        publicUrl,
        previewSrc,
        srcset,
        alt,
        width,
        height,
        thumbBounds,
      },
    ];
  }, [alt, height, index, previewSrc, publicUrl, slides, src, srcset, thumbBounds, width]);

  const { slides: preparedSlides, index: preparedIndex } = useMemo(
    () => (open ? preparePhotoSwipeSlides(resolvedSlides, index) : { slides: [], index: 0 }),
    [index, open, resolvedSlides],
  );

  useEffect(() => {
    if (!open || preparedSlides.length > 0) return;

    const photoSwipe = photoSwipeRef.current;
    if (photoSwipe) {
      shouldNotifyParentOnCloseRef.current = true;
      photoSwipe.close();
      return;
    }

    onCloseRef.current?.();
  }, [open, preparedSlides.length]);

  useEffect(() => {
    if (!open || preparedSlides.length === 0 || photoSwipeRef.current || typeof document === 'undefined') return;

    const photoSwipe = new PhotoSwipe({
      dataSource: preparedSlides,
      index: preparedIndex,
      mainClass: PHOTOSWIPE_MAIN_CLASS,
    });

    photoSwipe.addFilter('thumbBounds', (currentBounds, itemData) => itemData?.thumbBounds || currentBounds);
    photoSwipe.on('uiRegister', () => {
      if (!hasShowInChat) return;

      photoSwipe.ui.registerElement({
        name: 'show-in-chat',
        order: 9,
        isButton: true,
        html: 'Show in Chat',
        onClick: () => {
          onShowInChatRef.current?.(photoSwipe.currSlide?.data || null);
        },
      });
    });
    photoSwipe.on('contentLoad', (event) => {
      const { content } = event;
      if (content.type !== 'pdf') return;

      event.preventDefault();

      const pdfSlide = document.createElement('div');
      pdfSlide.className = 'pswp__content pswp__pdf-slide';

      const pdfFrame = document.createElement('iframe');
      pdfFrame.className = 'pswp__pdf-frame';
      pdfFrame.src = `${getRequiredSlidePdfUrl(content.data)}#view=FitH&pagemode=none`;
      pdfFrame.title = content.data.alt || 'PDF';
      pdfSlide.appendChild(pdfFrame);

      content.element = pdfSlide;
    });
    photoSwipe.on('imageClickAction', (event) => {
      // let our menu own right click so photoswipe doesn't do its thing too.
      if (isContextMenuGesture(event.originalEvent)) {
        event.preventDefault();
      }
    });
    photoSwipe.on('bgClickAction', (event) => {
      if (isContextMenuGesture(event.originalEvent)) {
        event.preventDefault();
      }
    });
    const handleContextMenu = (e) => {
      const slide = photoSwipe.currSlide?.data;
      if (!slide || slide.type === 'pdf') return;

      // open our attachment menu and keep the lightbox sitting still.
      e.preventDefault();
      e.stopPropagation();
      setContextMenu({
        position: { x: e.clientX, y: e.clientY },
        attachment: {
          id: slide.attachmentId,
          public_url: slide.publicUrl,
          variants: { large: { public_url: slide.src } },
          metadata: { file_name: slide.alt, content_type: slide.contentType },
        },
      });
    };
    // Close context menu when the slide changes, specifically for the case where
    // you had it open, then hit the right arrow key — the slide changed but the
    // context menu data would be stale.
    photoSwipe.on('change', () => {
      setContextMenu(null);
    });
    photoSwipe.on('afterInit', () => {
      photoSwipe.element.addEventListener('contextmenu', handleContextMenu);
    });
    photoSwipe.on('destroy', () => {
      photoSwipe.element?.removeEventListener('contextmenu', handleContextMenu);
      if (photoSwipeRef.current === photoSwipe) {
        photoSwipeRef.current = null;
        if (isMountedRef.current) {
          // Cursor/Bugbot flagged a rapid close->reopen race here: close() animates before destroy,
          // so we bump a version on destroy to force the init effect to run again once the old
          // instance fully tears down and `open` is still true.
          setInstanceVersion((currentVersion) => currentVersion + 1);
        }
      }

      const shouldNotifyParent = shouldNotifyParentOnCloseRef.current;
      shouldNotifyParentOnCloseRef.current = true;
      if (shouldNotifyParent) {
        onCloseRef.current?.();
      }
    });

    shouldNotifyParentOnCloseRef.current = true;
    photoSwipeRef.current = photoSwipe;
    photoSwipe.init();
  }, [hasShowInChat, instanceVersion, open, preparedIndex, preparedSlides]);

  useEffect(() => {
    if (open) return;

    const photoSwipe = photoSwipeRef.current;
    if (!photoSwipe) return;

    shouldNotifyParentOnCloseRef.current = false;
    photoSwipe.close();
  }, [open]);

  useEffect(
    () => () => {
      const photoSwipe = photoSwipeRef.current;
      if (!photoSwipe) return;

      shouldNotifyParentOnCloseRef.current = false;
      photoSwipe.destroy();
      photoSwipeRef.current = null;
    },
    [],
  );

  return (
    <ContextMenuPortal
      isOpen={Boolean(contextMenu)}
      onClose={handleCloseContextMenu}
      position={contextMenu?.position ?? ZERO_POSITION}
      message={null}
      onAddReaction={null}
      clickedAttachment={contextMenu?.attachment ?? null}
      remixChannelId={channelId}
      onBeforeRemix={handleBeforeRemix}
      className="z-(--stack-messages-lightbox-context-menu)"
    />
  );
};

export default ImageAttachmentLightbox;
